export interface CommReportEmployeeRow {
  sc_emp_id: string;
  comm_plan_id: number;
  empname: string;
  sales_rep_id: string;
  employee_id: string;
  supervisor_name: string;
  manager_name: string;
  comm_plan: string;
  sales_channel_desc: string;
  pay_dt: string;
  commperiod: string;
  kpis: Record<string, {
    commission_val: number | string;
    revenue: number | string;
    percentage: number | string;
    goal: number | string;
    units: number | string;
  }>;
  total_commission: number;
  total_revenue: number;
}

export interface KpiColumnConfig {
  kpiName: string;
  viewOrder: number;
  includeKpi: boolean;
  showUnits: boolean;
  showCommission: boolean;
  showGoal: boolean;
  showPercentage: boolean;
  showRevenue: boolean;
  showTier: boolean;
}

export interface KpiCellDisplay {
  includeKpi: boolean;
  showUnits: boolean;
  showCommission: boolean;
  showGoal: boolean;
  showPercentage: boolean;
  showRevenue: boolean;
  showTier: boolean;
}

export type CommReportKpiConfigByPlan = Record<number, KpiColumnConfig[]>;

export interface CommReportPivotResult {
  pivotedData: CommReportEmployeeRow[];
  allKpiConfigs: KpiColumnConfig[];
  payrollMonth: string;
  grandTotalCommission: number;
}

export interface CommReportExcelPayload {
  title: string;
  headerRows: CommReportExcelHeaderRow[];
  headers: string[];
  data: unknown[][];
  highlightLastRow: boolean;
  applyCommissionFormatting: boolean;
  displayOnlyColumnNumbers?: number[];
  /** Freeze first N columns (demographics) when scrolling horizontally in Excel. */
  freezeColumnCount?: number;
  /** Per-column number format; length should match headers.length */
  columnFormats?: CommReportExcelColumnFormat[];
  /** Optional explicit Excel column widths (character units); skips auto-size for that column. */
  columnWidths?: number[];
}

export type CommReportExcelColumnFormat = 'text' | 'integer' | 'currency' | 'percent';

export interface CommReportExcelHeaderRow {
  label: string;
  value: string;
  highlight?: boolean;
  mergeSpan?: number;
}

export interface KpiColumnTotals {
  showUnits: boolean;
  showCommission: boolean;
  units: number;
  commission: number;
}

/** KPI shown for analysis only — excluded from Total Commission calculations. */
export const CHARGEBACK_KPI_NAME = 'Chargebacks';

export const CHARGEBACK_DISPLAY_NOTICE_TITLE = 'Chargebacks — For Analysis Only';

export const CHARGEBACK_DISPLAY_NOTICE =
  'The Chargebacks column shows chargeback activity for your review only. These amounts are not included in Total Commission because chargebacks are already reflected in your other KPI commission calculations. Showing them in the total would double-count and produce an incorrect amount.';

export const CHARGEBACK_DISPLAY_NOTICE_SHORT = 'Not included in Total Commission';

export function isDisplayOnlyKpi(kpiName: string): boolean {
  return kpiName === CHARGEBACK_KPI_NAME;
}

export function countsTowardTotalCommission(kpiName: string): boolean {
  return !isDisplayOnlyKpi(kpiName);
}

export function hasVisibleDisplayOnlyKpi(visibleConfigs: KpiColumnConfig[]): boolean {
  return visibleConfigs.some(c => isDisplayOnlyKpi(c.kpiName));
}

function buildExcelKpiHeader(config: KpiColumnConfig, suffix: string): string {
  if (isDisplayOnlyKpi(config.kpiName)) {
    return `${config.kpiName} ${suffix}\nFOR ANALYSIS ONLY\n${CHARGEBACK_DISPLAY_NOTICE_SHORT}`;
  }
  return `${config.kpiName} ${suffix}`;
}

/** Default display toggles for new KPI config rows. */
export const DEFAULT_KPI_DISPLAY_TOGGLES = {
  showUnits: true,
  showCommission: true,
  showGoal: true,
  showPercentage: true,
  showRevenue: false,
  showTier: false
};

export function hasAnyKpiDisplayToggle(config: Pick<KpiColumnConfig, 'showUnits' | 'showCommission' | 'showGoal' | 'showPercentage' | 'showRevenue' | 'showTier'>): boolean {
  return config.showUnits
    || config.showCommission
    || config.showGoal
    || config.showPercentage
    || config.showRevenue
    || config.showTier;
}

export function normalizeKpiColumnConfig(config: KpiColumnConfig): KpiColumnConfig {
  return {
    ...config,
    showGoal: config.showGoal ?? DEFAULT_KPI_DISPLAY_TOGGLES.showGoal,
    showPercentage: config.showPercentage ?? DEFAULT_KPI_DISPLAY_TOGGLES.showPercentage,
    showRevenue: config.showRevenue ?? DEFAULT_KPI_DISPLAY_TOGGLES.showRevenue,
    showTier: config.showTier ?? DEFAULT_KPI_DISPLAY_TOGGLES.showTier
  };
}

export function formatKpiPercentage(value: number | string | null | undefined): string {
  if (value === null || value === undefined || value === '') {
    return '';
  }
  const num = parseFloat(String(value));
  if (Number.isNaN(num)) {
    return '';
  }
  return `${num.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}%`;
}

/** Numeric performance value for Excel percent formatting (115.8 → 1.158 when pct fmt applied). */
export function parseKpiPercentageValue(value: number | string | null | undefined): number | '' {
  if (value === null || value === undefined || value === '') {
    return '';
  }
  const num = parseFloat(String(value));
  if (Number.isNaN(num)) {
    return '';
  }
  return num > 1 ? num / 100 : num;
}

export function formatKpiNumber(value: number | string | null | undefined): string {
  if (value === null || value === undefined || value === '') {
    return '';
  }
  const num = parseFloat(String(value));
  if (Number.isNaN(num)) {
    return '';
  }
  return num.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 });
}

/** All KPIs visible when no saved plan configuration exists yet. */
export function buildAllOnKpiConfigs(kpiNames: string[], viewOrders: Map<string, number>): KpiColumnConfig[] {
  return kpiNames.map(kpiName => ({
    kpiName,
    viewOrder: viewOrders.get(kpiName) || 0,
    includeKpi: true,
    ...DEFAULT_KPI_DISPLAY_TOGGLES
  }));
}

export function pivotCommReportData(rawData: any[]): CommReportPivotResult {
  const empty: CommReportPivotResult = {
    pivotedData: [],
    allKpiConfigs: [],
    payrollMonth: '',
    grandTotalCommission: 0
  };

  if (!rawData || rawData.length === 0) {
    return empty;
  }

  const kpiSet = new Map<string, number>();
  rawData.forEach(row => {
    if (row.kpiname && !kpiSet.has(row.kpiname)) {
      kpiSet.set(row.kpiname, row.view_order);
    }
  });

  const sortedKpiNames = Array.from(kpiSet.keys()).sort(
    (a, b) => (kpiSet.get(b) || 0) - (kpiSet.get(a) || 0)
  );

  const employeeMap = new Map<string, CommReportEmployeeRow>();

  rawData.forEach(row => {
    const empId = row.sc_emp_id || row.sales_rep_id || row.employee_id;
    if (!empId) {
      return;
    }

    if (!employeeMap.has(empId)) {
      employeeMap.set(empId, {
        sc_emp_id: empId,
        comm_plan_id: Number(row.comm_plan_id) || 0,
        empname: row.empname,
        sales_rep_id: row.sales_rep_id,
        employee_id: row.employee_id,
        supervisor_name: row.supervisor_name,
        manager_name: row.manager_name,
        comm_plan: row.comm_plan,
        sales_channel_desc: row.sales_channel_desc,
        pay_dt: row.pay_dt,
        commperiod: row.commperiod,
        kpis: {},
        total_commission: 0,
        total_revenue: 0
      });
    }

    const emp = employeeMap.get(empId)!;
    emp.kpis[row.kpiname] = {
      commission_val: row.commission_val,
      revenue: row.revenue,
      percentage: row.percentage,
      goal: row.goal,
      units: row.units
    };

    emp.total_commission += parseFloat(row.commission_val || 0);
    emp.total_revenue += parseFloat(row.revenue || 0);
  });

  const pivotedData = Array.from(employeeMap.values());
  const grandTotalCommission = pivotedData.reduce((sum, emp) => sum + emp.total_commission, 0);
  const payrollMonth = resolvePayrollMonthFromCommperiod(pivotedData[0]?.commperiod);

  return {
    pivotedData,
    allKpiConfigs: buildAllOnKpiConfigs(sortedKpiNames, kpiSet),
    payrollMonth,
    grandTotalCommission
  };
}

export function mergeSavedKpiConfigs(
  availableKpis: KpiColumnConfig[],
  savedConfigs: KpiColumnConfig[]
): KpiColumnConfig[] {
  if (!savedConfigs || savedConfigs.length === 0) {
    return sortKpiConfigsByViewOrder(availableKpis.map(c => ({ ...c })));
  }

  const savedMap = new Map(savedConfigs.map(c => [c.kpiName, c]));

  const merged = availableKpis.map(available => {
    const saved = savedMap.get(available.kpiName);
    if (!saved) {
      return { ...available };
    }

    return {
      ...available,
      includeKpi: saved.includeKpi,
      showUnits: saved.showUnits,
      showCommission: saved.showCommission,
      showGoal: saved.showGoal ?? DEFAULT_KPI_DISPLAY_TOGGLES.showGoal,
      showPercentage: saved.showPercentage ?? DEFAULT_KPI_DISPLAY_TOGGLES.showPercentage,
      showRevenue: saved.showRevenue ?? DEFAULT_KPI_DISPLAY_TOGGLES.showRevenue,
      showTier: saved.showTier ?? DEFAULT_KPI_DISPLAY_TOGGLES.showTier,
      viewOrder: typeof saved.viewOrder === 'number' ? saved.viewOrder : available.viewOrder
    };
  });

  return sortKpiConfigsByViewOrder(merged);
}

/** Higher viewOrder = further left in the report. List is sorted top-to-bottom = left-to-right. */
export function sortKpiConfigsByViewOrder(configs: KpiColumnConfig[]): KpiColumnConfig[] {
  return [...configs].sort((a, b) => b.viewOrder - a.viewOrder);
}

export function applyKpiConfigOrderFromList(configs: KpiColumnConfig[]): KpiColumnConfig[] {
  return configs.map((config, index) => ({
    ...config,
    viewOrder: configs.length - index
  }));
}

export function buildConfigsByPlanFromResponse(
  commPlanIds: number[],
  allKpis: KpiColumnConfig[],
  apiResponse: CommReportKpiConfigByPlan
): CommReportKpiConfigByPlan {
  const result: CommReportKpiConfigByPlan = {};

  commPlanIds.forEach(planId => {
    const numericPlanId = Number(planId);
    const saved = apiResponse?.[numericPlanId]
      || apiResponse?.[planId as unknown as number]
      || (apiResponse as Record<string, KpiColumnConfig[]>)?.[String(planId)]
      || [];
    result[numericPlanId] = mergeSavedKpiConfigs(allKpis, saved);
  });

  return result;
}

export function getUnionVisibleKpiConfigs(
  configsByPlan: CommReportKpiConfigByPlan,
  commPlanIds: number[]
): KpiColumnConfig[] {
  const kpiMap = new Map<string, KpiColumnConfig>();

  commPlanIds.forEach(planId => {
    const planConfigs = configsByPlan[planId] || [];
    planConfigs.forEach(config => {
      if (!config.includeKpi || !hasAnyKpiDisplayToggle(config)) {
        return;
      }

      const existing = kpiMap.get(config.kpiName);
      if (!existing) {
        kpiMap.set(config.kpiName, { ...config });
        return;
      }

      existing.showUnits = existing.showUnits || config.showUnits;
      existing.showCommission = existing.showCommission || config.showCommission;
      existing.showGoal = existing.showGoal || config.showGoal;
      existing.showPercentage = existing.showPercentage || config.showPercentage;
      existing.showRevenue = existing.showRevenue || config.showRevenue;
      existing.showTier = existing.showTier || config.showTier;
      existing.viewOrder = Math.max(existing.viewOrder, config.viewOrder);
    });
  });

  return Array.from(kpiMap.values()).sort((a, b) => b.viewOrder - a.viewOrder);
}

export function getKpiCellDisplay(
  configsByPlan: CommReportKpiConfigByPlan,
  commPlanId: number,
  kpiName: string,
  unionConfig: KpiColumnConfig
): KpiCellDisplay {
  const planConfig = (configsByPlan[commPlanId] || []).find(c => c.kpiName === kpiName);

  if (!planConfig || !planConfig.includeKpi) {
    return {
      includeKpi: false,
      showUnits: false,
      showCommission: false,
      showGoal: false,
      showPercentage: false,
      showRevenue: false,
      showTier: false
    };
  }

  return {
    includeKpi: true,
    showUnits: unionConfig.showUnits && planConfig.showUnits,
    showCommission: unionConfig.showCommission && planConfig.showCommission,
    showGoal: unionConfig.showGoal && planConfig.showGoal,
    showPercentage: unionConfig.showPercentage && planConfig.showPercentage,
    showRevenue: unionConfig.showRevenue && planConfig.showRevenue,
    showTier: unionConfig.showTier && planConfig.showTier
  };
}

export function getVisibleKpiConfigs(configs: KpiColumnConfig[]): KpiColumnConfig[] {
  return configs.filter(c => c.includeKpi && hasAnyKpiDisplayToggle(c));
}

function buildDefaultCellDisplay(config: KpiColumnConfig): KpiCellDisplay {
  return {
    includeKpi: true,
    showUnits: config.showUnits,
    showCommission: config.showCommission,
    showGoal: config.showGoal,
    showPercentage: config.showPercentage,
    showRevenue: config.showRevenue,
    showTier: config.showTier
  };
}

function appendKpiExcelHeaders(
  headers: string[],
  columnFormats: CommReportExcelColumnFormat[],
  displayOnlyColumnNumbers: number[],
  config: KpiColumnConfig,
  columnNumberRef: { value: number }
): void {
  const append = (suffix: string, format: CommReportExcelColumnFormat) => {
    headers.push(buildExcelKpiHeader(config, suffix));
    columnFormats.push(format);
    if (isDisplayOnlyKpi(config.kpiName)) {
      displayOnlyColumnNumbers.push(columnNumberRef.value);
    }
    columnNumberRef.value++;
  };

  if (config.showUnits) {
    append('(Units)', 'integer');
  }
  if (config.showGoal) {
    append('(Goal)', 'integer');
  }
  if (config.showPercentage) {
    append('(Performance %)', 'percent');
  }
  if (config.showTier) {
    append('(Tier)', 'text');
  }
  if (config.showRevenue) {
    append('(Revenue)', 'currency');
  }
  if (config.showCommission) {
    append('($)', 'currency');
  }
}

function appendKpiExcelRowValues(
  row: unknown[],
  kpiData: CommReportEmployeeRow['kpis'][string] | undefined,
  config: KpiColumnConfig,
  cell: KpiCellDisplay
): void {
  if (config.showUnits) {
    row.push(cell.showUnits ? (kpiData?.units ?? '') : '');
  }
  if (config.showGoal) {
    row.push(cell.showGoal ? (kpiData?.goal ?? '') : '');
  }
  if (config.showPercentage) {
    row.push(cell.showPercentage ? parseKpiPercentageValue(kpiData?.percentage) : '');
  }
  if (config.showTier) {
    row.push(cell.showTier ? '' : '');
  }
  if (config.showRevenue) {
    row.push(cell.showRevenue ? (kpiData?.revenue ?? '') : '');
  }
  if (config.showCommission) {
    row.push(cell.showCommission ? (kpiData?.commission_val ?? '') : '');
  }
}

export function resolvePayrollMonthFromCommperiod(commperiod: string): string {
  if (!commperiod) {
    return '';
  }

  const dateParts = commperiod.split('  -  ');
  if (dateParts.length !== 2) {
    return '';
  }

  const validToDt = dateParts[1].trim();
  const parts = validToDt.split('-');
  if (parts.length !== 3) {
    return '';
  }

  const monthNum = parseInt(parts[0], 10);
  const year = parts[2];
  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  if (monthNum < 1 || monthNum > 12) {
    return '';
  }

  return `${monthNames[monthNum - 1]}/${year}`;
}

export function buildCommissionReportExcelFilename(payrollMonth: string, commperiod: string): string {
  if (payrollMonth) {
    return `Commission Report – ${payrollMonth}`;
  }

  const resolved = resolvePayrollMonthFromCommperiod(commperiod);
  return resolved ? `Commission Report – ${resolved}` : 'Commission_Report';
}

export function calculateKpiColumnTotals(
  rows: CommReportEmployeeRow[],
  visibleConfigs: KpiColumnConfig[],
  configsByPlan: CommReportKpiConfigByPlan = {}
): KpiColumnTotals[] {
  return visibleConfigs.map(config => {
    let units = 0;
    let commission = 0;
    let showUnits = false;
    let showCommission = false;

    rows.forEach(emp => {
      const cell = configsByPlan
        ? getKpiCellDisplay(configsByPlan, emp.comm_plan_id, config.kpiName, config)
        : buildDefaultCellDisplay(config);

      if (cell.showUnits) {
        showUnits = true;
        units += parseFloat(String(emp.kpis[config.kpiName]?.units || 0));
      }
      if (cell.showCommission) {
        showCommission = true;
        commission += parseFloat(String(emp.kpis[config.kpiName]?.commission_val || 0));
      }
    });

    return { showUnits, showCommission, units, commission };
  });
}

export function buildCommissionReportExcelPayload(
  pivotedData: CommReportEmployeeRow[],
  visibleConfigs: KpiColumnConfig[],
  payrollMonth: string,
  grandTotalCommission: number,
  configsByPlan?: CommReportKpiConfigByPlan
): CommReportExcelPayload {
  const headers = ['#', 'Employee Name', 'Sales Rep ID', 'Employee ID', 'Supervisor', 'Manager', 'Commission Plan'];
  const columnFormats: CommReportExcelColumnFormat[] = [
    'integer', 'text', 'text', 'text', 'text', 'text', 'text'
  ];
  const displayOnlyColumnNumbers: number[] = [];
  const columnNumberRef = { value: 8 };

  visibleConfigs.forEach(config => {
    appendKpiExcelHeaders(headers, columnFormats, displayOnlyColumnNumbers, config, columnNumberRef);
  });
  headers.push('Total Commission');
  columnFormats.push('currency');

  const data: unknown[][] = [];
  pivotedData.forEach((emp, index) => {
    const row: unknown[] = [
      index + 1,
      emp.empname,
      emp.sales_rep_id,
      emp.employee_id,
      emp.supervisor_name,
      emp.manager_name,
      emp.comm_plan
    ];

    visibleConfigs.forEach(config => {
      const cell = configsByPlan
        ? getKpiCellDisplay(configsByPlan, emp.comm_plan_id, config.kpiName, config)
        : buildDefaultCellDisplay(config);
      const kpiData = emp.kpis[config.kpiName];
      appendKpiExcelRowValues(row, kpiData, config, cell);
    });

    row.push(emp.total_commission);
    data.push(row);
  });

  const columnTotals = calculateKpiColumnTotals(
    pivotedData,
    visibleConfigs,
    configsByPlan || {}
  );

  const totalRow: unknown[] = ['', '', '', '', '', '', 'Total Commissions to be Paid:'];
  visibleConfigs.forEach((config, index) => {
    const colTotal = columnTotals[index];
    if (config.showUnits) {
      totalRow.push(colTotal.showUnits ? colTotal.units : '');
    }
    if (config.showGoal) {
      totalRow.push('');
    }
    if (config.showPercentage) {
      totalRow.push('');
    }
    if (config.showTier) {
      totalRow.push('');
    }
    if (config.showRevenue) {
      totalRow.push('');
    }
    if (config.showCommission) {
      totalRow.push(colTotal.showCommission ? colTotal.commission : '');
    }
  });
  totalRow.push(grandTotalCommission);
  data.push(totalRow);

  const commperiod = pivotedData[0]?.commperiod || '';
  const headerRows: CommReportExcelHeaderRow[] = [{ label: 'Payroll Month:', value: payrollMonth }];

  if (hasVisibleDisplayOnlyKpi(visibleConfigs)) {
    headerRows.push({
      label: `IMPORTANT: ${CHARGEBACK_DISPLAY_NOTICE_TITLE}`,
      value: CHARGEBACK_DISPLAY_NOTICE,
      highlight: true,
      mergeSpan: headers.length
    });
  }

  // Compact identity columns so KPI metrics are visible without dragging column borders.
  // Freeze only # + name + sales rep id; remaining demographics scroll with KPIs.
  // KPI columns are left unset so export auto-sizes from header/data (not the notice row).
  const columnWidths: number[] = [
    5,   // #
    22,  // Employee Name
    12,  // Sales Rep ID
    12,  // Employee ID
    18,  // Supervisor
    18,  // Manager
    20   // Commission Plan
  ];

  return {
    title: buildCommissionReportExcelFilename(payrollMonth, commperiod),
    headerRows,
    headers,
    data,
    highlightLastRow: true,
    applyCommissionFormatting: true,
    displayOnlyColumnNumbers,
    freezeColumnCount: 3,
    columnFormats,
    columnWidths
  };
}

export function getKpiColumnSubtitleParts(config: KpiColumnConfig): string[] {
  const parts: string[] = [];
  if (config.showUnits) {
    parts.push('Units');
  }
  if (config.showGoal) {
    parts.push('Goal');
  }
  if (config.showPercentage) {
    parts.push('Performance %');
  }
  if (config.showTier) {
    parts.push('Tier');
  }
  if (config.showRevenue) {
    parts.push('Revenue');
  }
  if (config.showCommission) {
    parts.push('Commission ($)');
  }
  return parts;
}

export function getKpiCellDisplayLines(
  kpiData: CommReportEmployeeRow['kpis'][string] | undefined,
  cell: KpiCellDisplay,
  kpiName: string
): { label: string; value: string; cssClass?: string }[] {
  const lines: { label: string; value: string; cssClass?: string }[] = [];

  if (cell.showUnits) {
    lines.push({ label: 'Units', value: formatKpiNumber(kpiData?.units) || '0' });
  }
  if (cell.showGoal) {
    lines.push({ label: 'Goal', value: formatKpiNumber(kpiData?.goal) || 'N/A' });
  }
  if (cell.showPercentage) {
    lines.push({
      label: 'Performance %',
      value: formatKpiPercentage(kpiData?.percentage) || 'N/A'
    });
  }
  if (cell.showTier) {
    lines.push({ label: 'Tier', value: 'N/A' });
  }
  if (cell.showRevenue) {
    lines.push({ label: 'Revenue', value: formatKpiNumber(kpiData?.revenue) || 'N/A' });
  }
  if (cell.showCommission) {
    const commission = parseFloat(String(kpiData?.commission_val || 0));
    lines.push({
      label: 'Commission',
      value: Number.isNaN(commission) ? 'N/A' : commission.toLocaleString(undefined, {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }),
      cssClass: isDisplayOnlyKpi(kpiName)
        ? 'chargeback-commission-value'
        : (cell.showUnits ? 'text-success font-weight-bold' : '')
    });
  }

  return lines;
}

export function ensureKpiConfigValid(config: KpiColumnConfig): void {
  if (config.showGoal == null) {
    config.showGoal = DEFAULT_KPI_DISPLAY_TOGGLES.showGoal;
  }
  if (config.showPercentage == null) {
    config.showPercentage = DEFAULT_KPI_DISPLAY_TOGGLES.showPercentage;
  }
  if (config.showRevenue == null) {
    config.showRevenue = DEFAULT_KPI_DISPLAY_TOGGLES.showRevenue;
  }
  if (config.showTier == null) {
    config.showTier = DEFAULT_KPI_DISPLAY_TOGGLES.showTier;
  }
  if (config.includeKpi && !hasAnyKpiDisplayToggle(config)) {
    config.showCommission = true;
  }
}

export function recalculateEmployeeTotals(
  emp: CommReportEmployeeRow,
  configsByPlan: CommReportKpiConfigByPlan,
  unionConfigs: KpiColumnConfig[]
): number {
  let total = 0;

  unionConfigs.forEach(config => {
    const cell = getKpiCellDisplay(configsByPlan, emp.comm_plan_id, config.kpiName, config);
    if (cell.showCommission && countsTowardTotalCommission(config.kpiName)) {
      total += parseFloat(String(emp.kpis[config.kpiName]?.commission_val || 0));
    }
  });

  return total;
}

export function applyConfiguredTotals(
  pivotedData: CommReportEmployeeRow[],
  configsByPlan: CommReportKpiConfigByPlan,
  unionConfigs: KpiColumnConfig[]
): number {
  let grandTotal = 0;

  pivotedData.forEach(emp => {
    emp.total_commission = recalculateEmployeeTotals(emp, configsByPlan, unionConfigs);
    grandTotal += emp.total_commission;
  });

  return grandTotal;
}

export interface CommReportDisplayState {
  pivotedData: CommReportEmployeeRow[];
  filteredData: CommReportEmployeeRow[];
  configsByPlan: CommReportKpiConfigByPlan;
  visibleKpiConfigs: KpiColumnConfig[];
  allKpiConfigs: KpiColumnConfig[];
  payrollMonth: string;
  grandTotalCommission: number;
  filteredGrandTotal: number;
}

export function setupCommReportDisplay(
  rawData: any[],
  commPlanIds: number[],
  savedConfigs: CommReportKpiConfigByPlan
): CommReportDisplayState {
  const pivot = pivotCommReportData(rawData);
  const planIds = commPlanIds.length ? commPlanIds : extractCommPlanIdsFromData(pivot.pivotedData);
  const configsByPlan = buildConfigsByPlanFromResponse(planIds, pivot.allKpiConfigs, savedConfigs);
  const visibleKpiConfigs = getUnionVisibleKpiConfigs(configsByPlan, planIds);
  const grandTotalCommission = applyConfiguredTotals(pivot.pivotedData, configsByPlan, visibleKpiConfigs);

  return {
    pivotedData: pivot.pivotedData,
    filteredData: [...pivot.pivotedData],
    configsByPlan,
    visibleKpiConfigs,
    allKpiConfigs: pivot.allKpiConfigs,
    payrollMonth: pivot.payrollMonth,
    grandTotalCommission,
    filteredGrandTotal: grandTotalCommission
  };
}

function extractCommPlanIdsFromData(pivotedData: CommReportEmployeeRow[]): number[] {
  const ids = new Set<number>();
  pivotedData.forEach(emp => {
    if (emp.comm_plan_id) {
      ids.add(emp.comm_plan_id);
    }
  });
  return Array.from(ids);
}
